import React, { useMemo, useState } from 'react';
import { FlatList } from '@codehesion-za/headless';
import { CSVLink } from 'react-csv';
import { useGetAllUserPayments } from '@project/queries';
import {
  LoadingContainer,
  PartnersSidePannel,
  SearchField,
  DonationHistoryCard,
} from '@project/components';
import { PartnerDonationsCard } from '@project/components/atoms/partner-donations-card';

const RenderItem = ({
  item,
  index,
}: {
  item: { name: string; id: number; total_amount: string; logo: string; currency: string };
  index: number;
}) => (
  <DonationHistoryCard
    key={`${item.id}-${index}`}
    header={item.name}
    id={item.id}
    amount={`$${item.total_amount}`}
    logo={item.logo}
  />
);

const EmptyListComponent = <span>No donation summaries to load</span>;

export const PartnersDonationHistoryPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);
  const { data: allUserPaymentsData, isLoading } = useGetAllUserPayments(search, fromDate, toDate);

  const csvData = useMemo(() => {
    if (!allUserPaymentsData) return [];
    return allUserPaymentsData.map((item: any) => ({
      donationName: item.name ?? '',
      totalDonated: item.total_amount ?? '',
      DonationDate: item.created_at ?? '',
    }));
  }, [allUserPaymentsData]);

  const csvHeaders = [
    { label: 'Donation Name', key: 'donationName' },
    { label: 'Donation Date', key: 'DonationDate' },
    { label: 'Donation Amount', key: 'totalDonated' },
  ];

  const handleFromDateChange = (value: string) => {
    setFromDate(value || null);
  };

  const handleToDateChange = (value: string) => {
    setToDate(value || null);
  };

  const totalDonated = useMemo(() => {
    return (
      allUserPaymentsData?.reduce(
        (acc: number, item: { total_amount: string }) => acc + parseFloat(item.total_amount),
        0,
      ) || 0
    );
  }, [allUserPaymentsData]);

  return (
    <div className="grid h-screen flex-1 grid-cols-6 gap-y-10">
      <div className="col-span-6 mx-4 bg-white pt-10 lg:col-span-2 lg:pb-0 2xl:mx-8">
        <PartnersSidePannel />
      </div>
      <div className="col-span-6 bg-[#F5F5F5] pb-4 md:pl-4 lg:col-span-4">
        <div className="w-s ceen my-10 mx-4 flex flex-col md:mx-6">
          <p className="text-lg">Donations</p>
          <div className="mb-7 w-24 border-b-2 border-lightGrey" />
          <div className="mb-4 flex flex-wrap items-center">
            <div className="mb-2 flex-1 sm:mb-0">
              <SearchField
                className="mr-4 rounded-full border-[#D3D3D3] bg-[#EAEAEA]"
                placeholder="Search"
                onChange={setSearch}
              />
            </div>
            <div className="mb-2 flex-1 sm:mb-0">
              <input
                type="date"
                className="mr-4 rounded-full border-[#D3D3D3] bg-[#EAEAEA]"
                value={fromDate || ''}
                onChange={(e) => handleFromDateChange(e.target.value)}
              />
            </div>
            <div className="mb-2 flex-1 sm:mb-0">
              <input
                type="date"
                className="mr-4 rounded-full border-[#D3D3D3] bg-[#EAEAEA]"
                value={toDate || ''}
                onChange={(e) => handleToDateChange(e.target.value)}
              />
            </div>
            <div className="col-span-7 bg-[#F5F5F5] lg:col-span-2">
              <div className="flex justify-center self-end lg:justify-end">
                <CSVLink
                  headers={csvHeaders}
                  filename={`Donation History`}
                  data={csvData ?? []}
                  className="flex rounded bg-gold py-2 px-5 text-center hover:bg-gold"
                >
                  <p className="w-40 py-1.5 text-sm font-bold text-white">CSV EXPORT</p>
                </CSVLink>
              </div>
            </div>
            <div className="mb-2 mt-4 flex-1 sm:mb-0">
              <PartnerDonationsCard totalDonations={`$${totalDonated}`} />
            </div>
          </div>
          <LoadingContainer isLoading={isLoading}>
            <FlatList
              items={allUserPaymentsData ?? []}
              renderItem={RenderItem}
              ListEmptyComponent={EmptyListComponent}
            />
          </LoadingContainer>
        </div>
      </div>
    </div>
  );
};
